import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { CONTACT } from '../data';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/about', label: 'About' },
  { to: '/faq', label: 'FAQ' },
  { to: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-black/70 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2 font-display tracking-tight text-white">
          <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center font-bold text-sm">E</div>
          <span className="text-lg font-medium hidden sm:inline">Express Detailz</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={`text-sm tracking-wide transition-colors ${
                pathname === n.to ? 'text-white' : 'text-zinc-400 hover:text-white'
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href={`tel:${CONTACT.phoneRaw}`} data-testid="navbar-call" className="text-zinc-300 hover:text-white text-sm flex items-center gap-2">
            <Phone className="w-4 h-4" /> {CONTACT.phone}
          </a>
          <Link
            to="/book"
            data-testid="navbar-book-button"
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]"
          >
            Book Your Detail
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-5">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase()}`} className="text-zinc-200 text-base">
              {n.label}
            </Link>
          ))}
          <Link to="/book" data-testid="mobile-book-button" className="bg-blue-600 text-white text-center py-3 rounded-full font-medium">
            Book Your Detail
          </Link>
          <a href={`tel:${CONTACT.phoneRaw}`} className="text-zinc-400 text-sm flex items-center gap-2">
            <Phone className="w-4 h-4" /> {CONTACT.phone}
          </a>
        </div>
      )}
    </header>
  );
}
